// Copyright (c) 2026 Michael F. Kamprath
//
// splash.js -- tap-to-start overlay for audio unlock.
//
// Modern browsers (iOS Safari in particular) refuse to play audio until
// the user has interacted with the page. This full-screen overlay
// captures the first tap and passes it to the onDismiss callback, which
// will unlock the Howler.js audio context in Phase 1c.
//
// Listens for `click` (not pointerdown) to avoid the iOS Safari
// click-fall-through bug where removing the overlay on pointerdown
// causes the synthetic click to hit an element underneath.
//
// Localized: button label reads from t('splash.tapToStart', getLocale()).
// The splash is created AFTER ensureLocaleInitialized() in app.js, so
// the locale is already correct by the time we render -- including on
// first visits where browser-language auto-detect populated localStorage
// during init.  Brand title "Ewe Can Count" stays Latin in every locale.

import { getLocale } from '../settings.js';
import { t } from './strings/index.js';

/**
 * @typedef {Object} Splash
 * @property {HTMLElement} element
 * @property {() => void} dismiss
 * @property {boolean} dismissed
 */

/**
 * Create a tap-to-start splash overlay and attach it to `parent`.
 *
 * @param {HTMLElement} parent
 * @param {{ onDismiss?: () => void | Promise<void> }} [opts]
 * @returns {Splash}
 */
export function createSplash(parent, opts = {}) {
  if (!(parent instanceof HTMLElement)) {
    throw new TypeError('createSplash: parent must be an element');
  }

  const onDismiss = opts.onDismiss ?? (() => {});

  const overlay = document.createElement('div');
  overlay.className = 'splash';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-label', 'Ewe Can Count');

  const inner = document.createElement('div');
  inner.className = 'splash__inner';

  const title = document.createElement('h2');
  title.className = 'splash__title';
  title.textContent = 'Ewe Can Count';
  inner.appendChild(title);

  const locale = getLocale();
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'splash__button';
  button.textContent = t('splash.tapToStart', locale);
  button.lang = locale;
  inner.appendChild(button);

  overlay.appendChild(inner);
  parent.appendChild(overlay);

  let dismissed = false;

  function dismiss() {
    if (dismissed) return;
    dismissed = true;
    removeListeners();

    // Fire onDismiss synchronously from within the gesture handler so
    // audio unlock happens on the user-gesture stack.
    let result;
    try {
      result = onDismiss();
    } catch (err) {
      console.warn('[splash] onDismiss threw:', err);
    }
    if (result && typeof result.catch === 'function') {
      result.catch((err) => {
        console.warn('[splash] onDismiss rejected:', err);
      });
    }

    overlay.classList.add('splash--dismissed');
    const remove = () => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
    let removed = false;
    const onEnd = () => {
      if (removed) return;
      removed = true;
      overlay.removeEventListener('transitionend', onEnd);
      remove();
    };
    overlay.addEventListener('transitionend', onEnd);
    setTimeout(onEnd, 400);
  }

  const handleClick = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    dismiss();
  };
  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      dismiss();
    }
  };

  overlay.addEventListener('click', handleClick);
  overlay.addEventListener('keydown', handleKey);

  function removeListeners() {
    overlay.removeEventListener('click', handleClick);
    overlay.removeEventListener('keydown', handleKey);
  }

  try {
    button.focus({ preventScroll: true });
  } catch {
    /* non-fatal */
  }

  return {
    element: overlay,
    dismiss,
    get dismissed() {
      return dismissed;
    },
  };
}
